import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle
} from "react";
import {
  DragDropContext,
  Droppable,
  Draggable
} from "react-beautiful-dnd";
import { Card, CardBody, Badge } from "reactstrap";

const SelectionCard = ({ sel, index, onDelete, canEdit }) => (
  <Draggable draggableId={String(sel.id)} index={index} isDragDisabled={!canEdit}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...(canEdit ? provided.dragHandleProps : {})}
        style={{
          ...provided.draggableProps.style,
          opacity: snapshot.isDragging ? 0.8 : 1,
          cursor: canEdit ? "grab" : "default"
        }}
      >
        <Card className="mb-2">
          <CardBody className="p-2 d-flex justify-content-between align-items-center">
            <span>{sel.applicantName}</span>
            {canEdit && (
              <button
                type="button"
                className="btn btn-sm btn-link text-danger p-0"
                title="Quitar del tablero"
                onClick={() => onDelete(sel.id)}
              >
                <i className="fas fa-times" />
              </button>
            )}
          </CardBody>
        </Card>
      </div>
    )}
  </Draggable>
);

const SelectionBoard = forwardRef(function SelectionBoard(
  { courseId, vacancies = 0, onChange, currentUserRole }, ref
) {
  const [finalists, setFinalists] = useState([]);
  const [reserves,   setReserves] = useState([]);

  const canEdit = Array.isArray(currentUserRole)
    ? !currentUserRole.includes("job_head")
    : currentUserRole !== "job_head";

  const fetchSelections = useCallback(() => {
    if (!courseId) return;
    fetch(`/api/courses/${courseId}/selections`)
      .then(r => r.json())
      .then(data => {
        const finals = [];
        const reserv = [];
        data.forEach(s => {
          const item = {
            id:            s.id,
            applicantName: s.applicant.name,
            postulationId: s.postulation_id
          };
          (s.selection_type === "selected" ? finals : reserv).push(item);
        });
        setFinalists(finals);
        setReserves(reserv);
      });
  }, [courseId]);

  const changeType = (id, type) =>
    fetch(`/api/courses/${courseId}/selections/${id}`, {
      method : "PATCH",
      headers: {
        "X-CSRF-TOKEN": window.Hupe8.token,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ selection_type: type })
    });

  const demote    = id => changeType(id, "reserve");
  const promote   = id => changeType(id, "selected");

  const deleteSel = id =>
    fetch(`/api/courses/${courseId}/selections/${id}`, {
      method : "DELETE",
      headers: { "X-CSRF-TOKEN": window.Hupe8.token }
    }).then(fetchSelections);

  const onDragEnd = async ({ source, destination, draggableId }) => {
    if (!canEdit) return;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    if (destination.droppableId === "selected") {
      if (finalists.length < vacancies) {
        await promote(draggableId);
        fetchSelections();
        return;
      }

      const replaced = finalists[destination.index];
      if (replaced) {
        await Promise.all([
          demote(replaced.id),
          promote(draggableId)
        ]);
      }
      fetchSelections();
      return;
    }

    if (destination.droppableId === "reserve") {
      await demote(draggableId);
      fetchSelections();
    }
  };

  useEffect(fetchSelections, [courseId]);
  useEffect(() => { onChange?.(); }, [finalists, reserves]);

  useImperativeHandle(ref, () => ({
    fetchSelections,
    getSelectionMap: () =>
      Object.fromEntries([
        ...finalists.map(f => [f.postulationId, "selected"]),
        ...reserves.map(r  => [r.postulationId, "reserve"])
      ])
  }));

  const emptySlots = Math.max(0, vacancies - finalists.length);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="row">

        <div className="col-md-6 mb-3">
          <h5>
            Seleccionados&nbsp;
            <Badge color="primary">
              {finalists.length}/{vacancies}
            </Badge>
          </h5>

          <Droppable droppableId="selected" isDropDisabled={!canEdit}>
            {provided => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {finalists.map((s, i) => (
                  <SelectionCard
                    key={s.id}
                    sel={s}
                    index={i}
                    onDelete={deleteSel}
                    canEdit={canEdit}
                  />
                ))}

                {[...Array(emptySlots)].map((_, i) => (
                  <Card
                    key={`ph-${i}`}
                    className="mb-2 border border-2 border-dashed opacity-50"
                  >
                    <CardBody className="p-2 text-muted text-center">
                      Vacante libre
                    </CardBody>
                  </Card>
                ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        <div className="col-md-6 mb-3">
          <h5>
            Reservas&nbsp;
            <Badge color="secondary">{reserves.length}</Badge>
          </h5>

          <Droppable droppableId="reserve" isDropDisabled={!canEdit}>
            {provided => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {reserves.map((s, i) => (
                  <SelectionCard
                    key={s.id}
                    sel={s}
                    index={i}
                    onDelete={deleteSel}
                    canEdit={canEdit}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

      </div>
    </DragDropContext>
  );
});

export default SelectionBoard;
